const RESERVATION_COLORS = {
  'Family':     '#2980b9',
  'Individual': '#8e44ad',
  'Renter':     '#d35400',
  'default':    '#27ae60'
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const DOW_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Layout constants (px)
const DAY_NUM_H  = 38;  // height reserved at top of cell for day number
const EVENT_H    = 22;  // height of each event bar
const EVENT_GAP  = 3;   // vertical gap between stacked event bars
const BOTTOM_PAD = 8;   // padding below last event row

function pad(n) { return String(n).padStart(2, '0'); }

function toDateStr(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function getTodayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildWeeks(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow    = new Date(year, month, 1).getDay();
  const weeks = [];
  let cells = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(d);
    if (cells.length === 7 || d === daysInMonth) {
      while (cells.length < 7) cells.push(null);
      weeks.push([...cells]);
      cells = [];
    }
  }
  return weeks;
}

// Returns events for a week, each with startCol, endCol, eventRow, isFirstDay
function getWeekEvents(week, year, month, monthEvents) {
  const monthStartStr = toDateStr(year, month, 1);
  const weekDates     = week.map(day => (day ? toDateStr(year, month, day) : null));
  const validDates    = weekDates.filter(Boolean);
  if (!validDates.length) return [];

  const weekStart = validDates[0];
  const weekEnd   = validDates[validDates.length - 1];

  const eventsInWeek = monthEvents
    .filter(e => e.startStr <= weekEnd && e.endStr >= weekStart)
    .map(e => {
      // First column in this week that the event occupies
      const startCol = weekDates.findIndex(d => d !== null && d >= e.startStr);
      if (startCol === -1) return null;

      // Last column in this week that the event occupies
      let endCol = -1;
      for (let i = 6; i >= 0; i--) {
        if (weekDates[i] !== null && weekDates[i] <= e.endStr) { endCol = i; break; }
      }
      if (endCol === -1 || endCol < startCol) return null;

      // Show title only on the first cell we render this event in this month
      const effectiveStart = e.startStr >= monthStartStr ? e.startStr : monthStartStr;
      const isFirstDay = weekDates[startCol] === effectiveStart;

      return { ...e, startCol, endCol, isFirstDay };
    })
    .filter(Boolean)
    .sort((a, b) =>
      a.startCol !== b.startCol
        ? a.startCol - b.startCol
        : (b.endCol - b.startCol) - (a.endCol - a.startCol) // longer first
    );

  // Greedy row assignment (pack events upward)
  const rowEndCols = [];
  for (const ev of eventsInWeek) {
    let placed = false;
    for (let r = 0; r < rowEndCols.length; r++) {
      if (ev.startCol > rowEndCols[r]) {
        ev.eventRow = r;
        rowEndCols[r] = ev.endCol;
        placed = true;
        break;
      }
    }
    if (!placed) {
      ev.eventRow = rowEndCols.length;
      rowEndCols.push(ev.endCol);
    }
  }

  return eventsInWeek;
}

// Convert column index to CSS percentage string (1/7 units)
function colPct(n) { return (n / 7 * 100).toFixed(5) + '%'; }

function renderMonth(year, month, events, todayStr, generatedStr) {
  const daysInMonth   = new Date(year, month + 1, 0).getDate();
  const monthStartStr = toDateStr(year, month, 1);
  const monthEndStr   = toDateStr(year, month, daysInMonth);

  const monthEvents = events.filter(e =>
    e.startStr <= monthEndStr && e.endStr >= monthStartStr
  );

  const weeks = buildWeeks(year, month);

  // ── DOW header row ──────────────────────────────────────────────────────────
  const dowCells = DOW_LABELS.map((d, i) => {
    const sep = i < 6 ? 'border-right:1.5px solid #bbb;' : '';
    return `<div style="flex:1;text-align:center;padding:10px 4px;font-size:13px;font-weight:bold;color:#555;background:#f0f0f0;font-family:Helvetica,Arial,sans-serif;${sep}">${d}</div>`;
  }).join('');

  // ── Week rows ───────────────────────────────────────────────────────────────
  const weekRowsHtml = weeks.map((week, wi) => {
    const isLastWeek = wi === weeks.length - 1;
    const weekDates  = week.map(day => (day ? toDateStr(year, month, day) : null));
    const weekEvents = getWeekEvents(week, year, month, monthEvents);
    const numEvtRows = weekEvents.length
      ? Math.max(...weekEvents.map(e => e.eventRow)) + 1
      : 0;
    const rowH = DAY_NUM_H + numEvtRows * (EVENT_H + EVENT_GAP) + BOTTOM_PAD;

    // Cell backgrounds (gray for empty, blue tint for today, white otherwise)
    const bgCells = week.map((day, di) => {
      const bg = !day
        ? '#f7f7f7'
        : weekDates[di] === todayStr ? '#deeffe' : 'white';
      return `<div style="position:absolute;left:${colPct(di)};width:${colPct(1)};top:0;bottom:0;background:${bg};"></div>`;
    }).join('');

    // Vertical column separators (1px lines between columns)
    const separators = Array.from({ length: 6 }, (_, i) =>
      `<div style="position:absolute;left:${colPct(i + 1)};top:0;bottom:0;width:1px;background:#ccc;"></div>`
    ).join('');

    // Day numbers (absolutely positioned in their column, right-aligned)
    const dayNums = week.map((day, di) => {
      if (!day) return '';
      const isToday = weekDates[di] === todayStr;
      const numHtml = isToday
        ? `<span style="display:inline-block;background:#2980b9;color:#fff;border-radius:50%;width:27px;height:27px;line-height:27px;text-align:center;font-size:15px;">${day}</span>`
        : day;
      return `<div style="position:absolute;left:${colPct(di)};width:${colPct(1)};top:0;height:${DAY_NUM_H}px;padding:5px 7px 0;text-align:right;font-size:17px;font-weight:bold;color:#333;font-family:Helvetica,Arial,sans-serif;">${numHtml}</div>`;
    }).join('');

    // Event bars — span from startCol to endCol, crossing cell boundaries
    const eventBars = weekEvents.map(ev => {
      const color  = RESERVATION_COLORS[ev.type] || RESERVATION_COLORS['default'];
      // 2px inner gap from column edge so the bar doesn't touch separator lines
      const left  = `calc(${colPct(ev.startCol)} + 2px)`;
      const width = `calc(${colPct(ev.endCol - ev.startCol + 1)} - 4px)`;
      const top   = DAY_NUM_H + ev.eventRow * (EVENT_H + EVENT_GAP);
      const title = ev.isFirstDay ? escapeHtml(ev.title) : '';

      return `<div style="position:absolute;left:${left};width:${width};top:${top}px;height:${EVENT_H}px;background:${color};color:#fff;font-size:11px;font-weight:bold;padding:0 6px;border-radius:3px;line-height:${EVENT_H}px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-family:Helvetica,Arial,sans-serif;">${title}</div>`;
    }).join('');

    const bb = isLastWeek ? '' : 'border-bottom:1px solid #ccc;';
    return `<div style="position:relative;height:${rowH}px;${bb}">${bgCells}${separators}${dayNums}${eventBars}</div>`;
  }).join('');

  // ── Legend ──────────────────────────────────────────────────────────────────
  const legendItems = Object.entries(RESERVATION_COLORS)
    .filter(([k]) => k !== 'default')
    .map(([type, color]) =>
      `<span style="display:inline-flex;align-items:center;gap:5px;margin-right:18px;">
        <span style="display:inline-block;width:13px;height:13px;border-radius:3px;background:${color};flex-shrink:0;"></span>
        <span style="font-size:11px;font-weight:bold;color:#444;font-family:Helvetica,Arial,sans-serif;">${type}</span>
      </span>`
    ).join('');

  return `
    <div class="month-page">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid #333;">
        <div>
          <div style="font-size:10px;color:#888;margin-bottom:4px;font-family:Helvetica,Arial,sans-serif;">Papa's Palace — Reservation Calendar</div>
          <div style="font-size:34px;font-weight:bold;color:#1a1a1a;font-family:Helvetica,Arial,sans-serif;">${MONTH_NAMES[month]} ${year}</div>
        </div>
        <div style="font-size:10px;color:#aaa;text-align:right;font-family:Helvetica,Arial,sans-serif;">Generated ${escapeHtml(generatedStr)}</div>
      </div>

      <div style="border:1px solid #999;">
        <div style="display:flex;border-bottom:2px solid #999;">${dowCells}</div>
        ${weekRowsHtml}
      </div>

      <div style="margin-top:12px;padding-top:8px;border-top:1px solid #ccc;display:flex;flex-wrap:wrap;align-items:center;">
        ${legendItems}
      </div>
    </div>
  `;
}

export function printCalendar(year, startMonth, endMonth, events, generatedAt) {
  const todayStr      = getTodayStr();
  const generatedStr  = new Date(generatedAt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });

  const monthPages = [];
  for (let m = startMonth; m <= endMonth; m++) {
    monthPages.push(renderMonth(year, m, events, todayStr, generatedStr));
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Papa's Palace Calendar ${year}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Helvetica, Arial, sans-serif; background: white; }
    .month-page {
      padding: 32px 40px 24px;
      page-break-after: always;
      break-after: page;
    }
    .month-page:last-child {
      page-break-after: avoid;
      break-after: avoid;
    }
    @media print {
      @page { size: letter landscape; margin: 0.4in; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .month-page { padding: 0 0 20px; }
    }
  </style>
</head>
<body>
  ${monthPages.join('\n')}
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=720');
  if (!win) {
    alert('Popup blocked — please allow popups for this site to print the calendar.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}
